import React, { useEffect, useState } from 'react';
import { Search, Star, Plus, Archive, User, Trash2 } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import RosterVerificationControl from './RosterVerificationControl';

/** Shared org-panel tabs (Directory / Preferred / Programs) with identical
 * structure and worker cards for BOTH agent and employer portals. Each tab
 * takes a `role` prop and picks the matching org-scoped endpoints. */

export function SkeletonRows({ n = 3 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="h-14 bg-slate-100 animate-pulse rounded-xl" />
      ))}
    </div>
  );
}

export function EmptyState({ label }) {
  return <p className="text-sm text-slate-400 py-8 text-center">{label}</p>;
}

export function QuickCreateModal({ title, fields, onSave, onCancel }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    for (const f of fields) {
      if (f.required && !String(values[f.key] || '').trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try { await onSave(values); } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="quick-create-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-3">
        <h3 className="font-bold text-slate-900">{title}</h3>
        {fields.map((f) => (
          <div key={f.key}>
            <label className="text-xs font-medium text-slate-600">{f.label}{f.required && ' *'}</label>
            {f.textarea ? (
              <textarea value={values[f.key] || ''} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={3}
                data-testid={`qc-field-${f.key}`} />
            ) : (
              <input value={values[f.key] || ''} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid={`qc-field-${f.key}`} />
            )}
          </div>
        ))}
        <div className="flex gap-2 pt-1">
          <button onClick={submit} disabled={saving}
            className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            data-testid="qc-save">{saving ? 'Saving…' : 'Save'}</button>
          <button onClick={onCancel} className="flex-1 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
            data-testid="qc-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Worker Directory — identical search + cards for both portals
// ---------------------------------------------------------------------
export function OrgDirectoryTab({ role }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(false);

  const listPath = role === 'agent' ? '/api/agent/directory' : '/api/org/directory';

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (available) params.set('available_only', 'true');
      const qs = params.toString();
      setItems((await apiCall(`${listPath}${qs ? `?${qs}` : ''}`)).workers || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load();   }, []);

  const togglePreferred = async (w) => {
    try {
      if (role === 'agent') {
        await apiCall(`/api/agent/preferred-workers/${w._id}`,
          { method: w.preferred ? 'DELETE' : 'POST', body: w.preferred ? undefined : JSON.stringify({}) });
      } else if (w.preferred) {
        await apiCall(`/api/employer/preferred-workers/${w._id}`, { method: 'DELETE' });
      } else {
        await apiCall('/api/employer/preferred-workers', { method: 'POST', body: JSON.stringify({ worker_id: w._id }) });
      }
      setItems((prev) => prev.map((x) => (x._id === w._id ? { ...x, preferred: !x.preferred } : x)));
      toast.success(w.preferred ? 'Removed from Preferred' : 'Added to Preferred');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[16rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Search by name, email, or skill…"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="org-directory-search"
          />
        </div>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          <input type="checkbox" checked={available} onChange={(e) => setAvailable(e.target.checked)} />
          Available only
        </label>
        <button onClick={load} className="px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700" data-testid="org-directory-search-btn">Search</button>
      </div>
      {loading ? <SkeletonRows /> : items.length === 0 ? <EmptyState label="No workers match." /> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((w) => (
            <div key={w._id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-fuchsia-400 flex items-center justify-center text-white font-semibold">
                  {(w.full_name || w.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{w.full_name || w.email}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {(w.skills || []).slice(0, 2).join(', ') || '—'}
                    {w.availability_status ? ` · ${w.availability_status}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <RosterVerificationControl workerId={w._id} workerName={w.full_name || w.email} />
                <button
                  onClick={() => togglePreferred(w)}
                  title={w.preferred ? 'Remove from preferred' : 'Add to preferred'}
                  className={`p-2 rounded-lg transition ${w.preferred ? 'text-amber-500 hover:bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-slate-50'}`}
                  data-testid={`org-directory-star-${w._id}`}
                >
                  <Star className="w-5 h-5" fill={w.preferred ? 'currentColor' : 'none'} />
                </button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ---------------------------------------------------------------------
// Preferred workers — identical list for both portals
// ---------------------------------------------------------------------
export function OrgPreferredTab({ role }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      if (role === 'agent') {
        const res = await apiCall('/api/agent/preferred-workers');
        setItems((res.preferred || []).map((p) => ({
          key: p.id || p.worker_id, worker_id: p.worker_id,
          name: p.worker_name || p.worker_email, sub: p.notes || '—',
        })));
      } else {
        const res = await apiCall('/api/employer/preferred-workers');
        setItems((res.workers || []).map((w) => ({
          key: w._id || w.worker_id, worker_id: w._id || w.worker_id,
          name: w.full_name || w.name || w.email, sub: w.email || '—',
        })));
      }
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load();   }, []);

  const remove = async (workerId) => {
    const base = role === 'agent' ? '/api/agent/preferred-workers' : '/api/employer/preferred-workers';
    try { await apiCall(`${base}/${workerId}`, { method: 'DELETE' }); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-slate-900">Preferred Workers</h3>
      {loading ? <SkeletonRows /> : items.length === 0 ? <EmptyState label="No preferred workers yet. Star someone in the Directory to add them." /> :
        <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl" data-testid="org-preferred-list">
          {items.map((p) => (
            <li key={p.key} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <User className="w-4 h-4 text-slate-400" />
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{p.name}</p>
                  <p className="text-xs text-slate-500 truncate">{p.sub}</p>
                </div>
              </div>
              <button onClick={() => remove(p.worker_id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Remove"
                data-testid={`org-preferred-remove-${p.worker_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

// ---------------------------------------------------------------------
// Programs — identical list for both portals
// ---------------------------------------------------------------------
export function OrgProgramsTab({ role }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const listPath = role === 'agent' ? '/api/agent/programs' : '/api/employer/group-homes/programs';

  const load = async () => {
    setLoading(true);
    try { setItems((await apiCall(listPath)).programs || []); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load();   }, []);

  const archive = async (id) => {
    const path = role === 'agent'
      ? `/api/agent/programs/${id}/archive`
      : `/api/employer/group-homes/programs/${id}/deactivate`;
    try { await apiCall(path, { method: 'POST' }); toast.success('Archived'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Programs / Group Homes</h3>
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          data-testid="org-program-add">
          <Plus className="w-4 h-4" /> New program
        </button>
      </div>
      {loading ? <SkeletonRows /> : items.length === 0 ? <EmptyState label="No programs yet." /> :
        <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl" data-testid="org-programs-list">
          {items.map((p) => (
            <li key={p._id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="font-medium text-slate-900">{p.name}</p>
                <p className="text-xs text-slate-500">{p.category || p.service_type || 'General'}{p.description ? ` · ${String(p.description).slice(0, 80)}` : ''}</p>
              </div>
              <button onClick={() => archive(p._id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Archive">
                <Archive className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      }
      {showNew && (
        <QuickCreateModal
          title="New program"
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: role === 'agent' ? 'category' : 'service_type', label: role === 'agent' ? 'Category' : 'Service type' },
            { key: 'description', label: 'Description', textarea: true },
          ]}
          onSave={async (v) => { await apiCall(listPath, { method: 'POST', body: JSON.stringify(v) }); toast.success('Program created'); setShowNew(false); load(); }}
          onCancel={() => setShowNew(false)}
        />
      )}
    </div>
  );
}
